import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Compass, Mail, CheckCircle2, Send, MessageCircle, Instagram } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { SectionHeading, reveal } from "@/components/Section";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-ink border border-line px-4 py-3.5 text-sm text-bone placeholder:text-faint focus:outline-none focus:border-blaze transition-colors duration-200";

export default function JoinForm() {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", vehicle: "", experience: "rookie", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/members/join?lang=${lang}`, form);
      setDone(true);
      toast.success(t.join.successTitle);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.join.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="community" className="py-28 sm:py-36" data-testid="community-section">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <SectionHeading eyebrow={t.join.eyebrow} title={t.join.title} sub={t.join.sub} />
          <motion.div {...reveal} className="border border-line bg-surface/60 p-6 sm:p-7">
            <p className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-blaze mb-4">
              {t.join.hqTitle}
            </p>
            <div className="flex items-center gap-3 text-sm text-sand mb-3">
              <Compass size={15} className="text-ochre shrink-0" />
              {t.join.hqPlace}
            </div>
            <div className="flex items-center gap-3 text-sm text-sand">
              <Mail size={15} className="text-ochre shrink-0" />
              {t.join.hqMail}
            </div>
            <a
              href="https://wa.me/393780618293"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hq-whatsapp-link"
              className="mt-3 flex items-center gap-3 text-sm text-sand hover:text-blaze transition-colors w-max"
            >
              <MessageCircle size={15} className="text-ochre shrink-0" />
              {t.join.hqWhatsApp}
            </a>
            <a
              href="https://instagram.com/dust_republic"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hq-instagram-link"
              className="mt-3 flex items-center gap-3 text-sm text-sand hover:text-blaze transition-colors w-max"
            >
              <Instagram size={15} className="text-ochre shrink-0" />
              @dust_republic
            </a>
            <img src="/logo.png" alt="Dust Republic" className="mt-6 h-20 w-auto" />
          </motion.div>
        </div>

        <motion.div {...reveal} className="lg:col-span-7">
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="ok"
                data-testid="join-form-success-alert"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="h-full min-h-[380px] border border-[#4E8752]/50 bg-[#4E8752]/10 flex flex-col items-center justify-center text-center p-10"
              >
                <CheckCircle2 size={44} className="text-[#7FB383] mb-6" />
                <h3 className="font-display text-3xl sm:text-4xl font-extrabold uppercase text-bone">
                  {t.join.successTitle}
                </h3>
                <p className="mt-3 text-sm sm:text-base text-ash max-w-md">{t.join.successText}</p>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={submit}
                exit={{ opacity: 0, y: -16 }}
                className="border border-line bg-surface/60 p-6 sm:p-9 grid sm:grid-cols-2 gap-5"
                data-testid="join-form"
              >
                <div>
                  <label className="block font-telemetry text-[10px] uppercase tracking-[0.22em] text-sandmut mb-2">
                    {t.join.name}
                  </label>
                  <input
                    data-testid="join-form-input-name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder={t.join.namePh}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block font-telemetry text-[10px] uppercase tracking-[0.22em] text-sandmut mb-2">
                    {t.join.email}
                  </label>
                  <input
                    data-testid="join-form-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder={t.join.emailPh}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block font-telemetry text-[10px] uppercase tracking-[0.22em] text-sandmut mb-2">
                    {t.join.vehicle}
                  </label>
                  <input
                    data-testid="join-form-input-vehicle"
                    required
                    value={form.vehicle}
                    onChange={set("vehicle")}
                    placeholder={t.join.vehiclePh}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block font-telemetry text-[10px] uppercase tracking-[0.22em] text-sandmut mb-2">
                    {t.join.experience}
                  </label>
                  <select
                    data-testid="join-form-select-experience"
                    value={form.experience}
                    onChange={set("experience")}
                    className={`${inputCls} appearance-none cursor-pointer`}
                  >
                    {Object.entries(t.join.expOptions).map(([k, v]) => (
                      <option key={k} value={k} className="bg-ink">
                        {v}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block font-telemetry text-[10px] uppercase tracking-[0.22em] text-sandmut mb-2">
                    {t.join.message}
                  </label>
                  <textarea
                    data-testid="join-form-input-message"
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    placeholder={t.join.messagePh}
                    className={`${inputCls} resize-none`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <button
                    data-testid="join-form-submit-button"
                    type="submit"
                    disabled={sending}
                    className="group w-full sm:w-auto font-telemetry text-xs uppercase tracking-[0.22em] bg-blaze text-ink px-9 py-4 flex items-center justify-center gap-3 hover:bg-sand transition-colors duration-300 disabled:opacity-60"
                  >
                    {sending ? t.join.sending : t.join.submit}
                    <Send size={14} className="group-hover:translate-x-1 group-hover:-translate-y-0.5 transition-transform duration-300" />
                  </button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
